import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { getErrorMessage } from "@/lib/errorMessage";
import {
  Activity, LogOut, Loader2, Laptop, Smartphone, Globe, CheckCircle2,
} from "lucide-react";

/**
 * Session activity dialog — Feb 2026.
 *
 * Shows the user's active sessions + last-20 auth events. "Sign out
 * all other devices" revokes every other family, bumps
 * `token_generation` (kills other access tokens), and re-issues the
 * caller a fresh access token which we swap in transparently so THIS
 * session doesn't die too.
 */

function _relTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function _deviceIcon(label) {
  if (!label) return Globe;
  if (/iPhone|iPad|Android/i.test(label)) return Smartphone;
  return Laptop;
}

// Batch 4 · #4 (Feb 2026) — humanize the raw device_label into one
// of four plain-language buckets. Never surfaces raw user-agent
// strings (curl, HeadlessChrome, Mozilla/...) or technical
// identifiers to the ordinary operator. Uses ONLY existing data.
export function _humanizeDeviceLabel(label) {
  const raw = String(label || "").trim();
  if (!raw) return "Other device";
  // Automated / non-browser clients first — highest signal.
  if (/curl|wget|python|postman|node-fetch|axios|okhttp|httpie|libwww|automation|headless|puppeteer|playwright|selenium|phantom/i.test(raw)) {
    return "Automated session";
  }
  // Windows browsers → "This Windows browser" (the "this" is a
  // deliberate signal that the dialog talks about the machine the
  // operator is looking at).
  if (/windows/i.test(raw)) return "This Windows browser";
  // Everything else that names any common browser or platform is
  // just "Other browser".
  if (/mac ?os|macintosh|iphone|ipad|android|linux|chromium|chrome|firefox|safari|edge|opera|mozilla|browser/i.test(raw)) {
    return "Other browser";
  }
  return "Other device";
}

// Batch 4 · #4 · owner correction (Feb 2026) — recent-activity
// summaries embed long UA fragments like
// "Signed in from Mozilla/5.0 (Linux x86_64) AppleWebKit/537.36 …
//  HeadlessChrome/122.0.0.0 Safari/537.36".
//
// The first cut of the sanitizer used substring replacements, which
// left tail tokens like `Safari/537.36`, `Win64; x64) AppleWebKit`,
// or `x86_64)` visible on the surface — exactly what the owner
// caught on live re-verification.
//
// This second cut rebuilds the summary rather than patching it:
//   1. Match the whole "from <anything up to end-of-clause>" or
//      "at <anything>" clause and REPLACE THE ENTIRE CLAUSE with
//      ONE friendly label. All UA suffix text is discarded.
//   2. Any remaining IPv4 dotted-quad → "an unknown location".
//   3. Any residual bare UA token (Mozilla, AppleWebKit, Chrome,
//      HeadlessChrome, Puppeteer, Playwright, curl, python,
//      Win64, x86_64) → dropped by scrubbing the token run up to
//      the next sentence terminator.
export function _sanitizeSummary(summary) {
  const raw = String(summary || "").trim();
  if (!raw) return raw;

  const _UA_HINT = /(mozilla|applewebkit|headlesschrome|chromium|chrome\/|firefox\/|safari\/|edge\/|opera\/|puppeteer|playwright|selenium|phantomjs|curl\/|wget\/|python[-\s]?requests|python\/|okhttp\/|libwww|automation|win64|x86_64|khtml|gecko)/i;
  const _IPV4 = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/;

  const hasUA = _UA_HINT.test(raw);
  const hasIP = _IPV4.test(raw);
  if (!hasUA && !hasIP) return raw;

  // Bucket the whole raw string using the humanized helper — this is
  // the operator-facing label we substitute for every UA fragment.
  const bucket = _humanizeDeviceLabel(raw).toLowerCase();

  // Find the pivot at the FIRST " from " or " at " (case-insensitive)
  // and REBUILD as "<head> from <bucket>." — discarding ALL trailing
  // characters. This is deliberately blunt: any UA / IP shape that
  // survives becomes exactly one friendly clause.
  const m = raw.match(/^(.+?)\s+(from|at)\s+/i);
  const head = m ? m[1].trim() : "Signed in";
  return `${head} from ${bucket}.`;
}

function _actionLabel(a) {
  return {
    login_success: "Signed in",
    mfa_verify: "MFA verified",
    mfa_enroll: "MFA enabled",
    mfa_disable: "MFA disabled",
    password_change: "Password changed",
    sessions_signout_others: "Signed out other devices",
  }[a] || a;
}

export function SessionActivityDialog({ open, onOpenChange }) {
  const { loginWithToken, user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/sessions");
      setData(r.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not load session activity"));
    }
  }, []);

  useEffect(() => {
    if (open) {
      setData(null);
      refresh();
    }
  }, [open, refresh]);

  const doSignOutOthers = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/sessions/sign-out-others");
      // Swap in the freshly-minted access token so THIS session
      // survives the token_generation bump we just triggered.
      if (r.data?.access_token && r.data?.user) {
        loginWithToken(r.data.access_token, r.data.user);
      }
      const n = r.data?.revoked_families || 0;
      if (n === 0) {
        toast.success("No other devices to sign out");
      } else {
        toast.success(`Signed out ${n} other device${n === 1 ? "" : "s"}`);
      }
      setConfirmOpen(false);
      await refresh();
    } catch (err) {
      toast.error(getErrorMessage(err, "Sign-out failed"));
    } finally {
      setBusy(false);
    }
  };

  const sessions = data?.sessions || [];
  const events = data?.recent_events || [];
  const others = sessions.filter((s) => !s.is_current);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="session-activity-dialog"
          className="sm:max-w-lg max-h-[80vh] flex flex-col"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-emerald-400" />
              Session activity
            </DialogTitle>
            <DialogDescription>
              Where you're signed in and what's happened on your account
              recently. Sign out other devices any time.
            </DialogDescription>
          </DialogHeader>

          {!data && (
            <div className="flex items-center justify-center py-8"
                 data-testid="session-activity-loading">
              <Loader2 className="h-5 w-5 animate-spin text-zinc-500" />
            </div>
          )}

          {data && (
            <div
              className="space-y-5 flex-1 min-h-0 overflow-y-auto pr-1"
              data-testid="session-activity-dialog-body"
            >
              {/* ── Active sessions ─────────────────────────────────── */}
              <section data-testid="session-activity-sessions-section">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                  Active sessions <span className="text-zinc-600">({sessions.length})</span>
                </div>
                <div className="space-y-1.5">
                  {sessions.map((s) => {
                    // Batch 4 · #4 — humanize BEFORE any presentation
                    // decision so we never expose the raw
                    // user-agent to the ordinary operator, and so
                    // the icon uses the humanized bucket rather
                    // than the raw UA regex.
                    const humanLabel = _humanizeDeviceLabel(s.device_label);
                    const Icon = _deviceIcon(humanLabel);
                    return (
                      <div key={s.family_id}
                        data-testid={`session-row-${s.family_id}`}
                        className={`flex items-start gap-3 rounded-sm border p-2.5 ${
                          s.is_current
                            ? "border-emerald-900/60 bg-emerald-950/20"
                            : "border-zinc-800 bg-black/30"
                        }`}>
                        <Icon className={`h-4 w-4 mt-0.5 ${s.is_current ? "text-emerald-400" : "text-zinc-500"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2">
                            <span data-testid={`session-row-${s.family_id}-label`}
                                  className="truncate">{humanLabel}</span>
                            {s.is_current && (
                              <span data-testid={`session-row-${s.family_id}-current-badge`}
                                    className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300 border border-emerald-800 rounded-sm px-1.5 py-0.5">
                                current
                              </span>
                            )}
                          </div>
                          {/* Batch 4 · #4 — the raw IP is intentionally
                              NOT rendered to the ordinary operator.
                              We keep the "last active …" relative
                              time (already plain language). */}
                          <div className="text-xs text-zinc-500 mt-0.5"
                               data-testid={`session-row-${s.family_id}-when`}>
                            last active {_relTime(s.last_used_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {sessions.length === 0 && (
                    <div className="text-xs text-zinc-500 italic">
                      No active sessions found.
                    </div>
                  )}
                </div>
              </section>

              {/* ── Recent events ────────────────────────────────────── */}
              <section data-testid="session-activity-events-section">
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">
                  Recent activity <span className="text-zinc-600">({events.length})</span>
                </div>
                <div className="space-y-1">
                  {events.slice(0, 20).map((e, i) => (
                    <div key={`${e.ts}-${i}`}
                      data-testid={`event-row-${i}`}
                      className="flex items-center gap-2 text-xs text-zinc-400 py-1">
                      <CheckCircle2 className="h-3 w-3 text-zinc-600 shrink-0" />
                      <span className="font-medium text-zinc-300">{_actionLabel(e.action)}</span>
                      <span className="text-zinc-600">·</span>
                      <span className="truncate text-zinc-500 flex-1"
                            data-testid={`event-row-${i}-summary`}>
                        {_sanitizeSummary(e.summary)}
                      </span>
                      <span className="text-zinc-600 shrink-0 font-mono">{_relTime(e.ts)}</span>
                    </div>
                  ))}
                  {events.length === 0 && (
                    <div className="text-xs text-zinc-500 italic">
                      No recent activity.
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={busy || !data || others.length === 0}
              data-testid="sign-out-others-btn"
              className="rounded-sm w-full sm:w-auto">
              {busy
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <><LogOut className="h-4 w-4 mr-2" />
                    Sign out {others.length} other device{others.length === 1 ? "" : "s"}
                  </>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="sign-out-others-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out other devices?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll be signed out from every device except this one.
              Anyone using your account elsewhere will need to log in again.
              You will stay signed in here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sign-out-others-cancel-btn"
              disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="sign-out-others-confirm-btn"
              onClick={doSignOutOthers} disabled={busy}
              className="bg-red-700 hover:bg-red-600">
              {busy
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : "Sign out others"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default SessionActivityDialog;
